import { FC, useState } from 'react'
import { Alert, Button, Card, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd'
import {
  ControlOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery } from '@apollo/client'
import { arrayMove } from '@dnd-kit/sortable'
import { DragEndEvent } from '@dnd-kit/core'
import { confirm, translate } from '../../../core'
import { DraggableRow, DraggableTable, DragHandle } from '../../../core/component'
import {
  CreateShippingMethodMutation,
  DeleteShippingMethodMutation,
  GetShippingMethodsQuery,
  ReorderShippingMethodsMutation,
  RestoreShippingMethodMutation,
  ToggleActiveShippingMethodMutation,
  UpdateShippingMethodMutation,
} from '../graphql/shipping-method.graphql'
import {
  SHIPPING_METHOD_KIND_COLOR,
  SHIPPING_METHOD_KIND_LABEL,
  ShippingMethod,
  ShippingMethodEntry,
  ShippingMethodInput,
  ShippingMethodKind,
} from '../type/shipping-method'
import { ShippingEligibilityPreview } from './ShippingEligibilityPreview'
import { ShippingMethodModal } from './ShippingMethodModal'
import { ShippingMethodRulesDrawer } from './ShippingMethodRulesDrawer'

const PAGE = { take: 1000, skip: 0 }

export const ShippingMethodList: FC = () => {
  const [messageApi, contextHolder] = message.useMessage()
  const [showDeleted, setShowDeleted] = useState(false)
  const [rows, setRows] = useState<ShippingMethodEntry[]>([])
  const [editing, setEditing] = useState<ShippingMethod | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [rulesFor, setRulesFor] = useState<ShippingMethod | null>(null)

  const { loading, refetch } = useQuery(GetShippingMethodsQuery, {
    notifyOnNetworkStatusChange: true,
    variables: { request: { ...PAGE, includeDeleted: showDeleted } },
    onCompleted: result =>
      setRows(
        (result?.shippingMethodsFind.data ?? []).map(it => ({ key: it.id.toString(), method: it })),
      ),
    onError: error => messageApi.error(error.message),
  })

  const onError = (error: { message: string }) => messageApi.error(error.message)
  const onMutated = (msg: string) => {
    messageApi.success(translate(msg))
    refetch()
  }

  const [createMutation, { loading: creating }] = useMutation(CreateShippingMethodMutation, {
    onCompleted: () => {
      onMutated('save_success_message')
      closeModal()
    },
    onError,
  })
  const [updateMutation, { loading: updating }] = useMutation(UpdateShippingMethodMutation, {
    onCompleted: () => {
      onMutated('save_success_message')
      closeModal()
    },
    onError,
  })
  const [toggleMutation] = useMutation(ToggleActiveShippingMethodMutation, {
    onCompleted: () => onMutated('save_success_message'),
    onError,
  })
  const [reorderMutation] = useMutation(ReorderShippingMethodsMutation, { onError })
  const [deleteMutation] = useMutation(DeleteShippingMethodMutation, {
    onCompleted: () => onMutated('remove_success_message'),
    onError,
  })
  const [restoreMutation] = useMutation(RestoreShippingMethodMutation, {
    onCompleted: () => onMutated('save_success_message'),
    onError,
  })

  const openCreate = () => {
    setEditing(null)
    setModalOpen(true)
  }

  const openEdit = (record: ShippingMethod) => {
    setEditing(record)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditing(null)
  }

  const handleSubmit = (dto: ShippingMethodInput) => {
    if (editing)
      updateMutation({ variables: { id: editing.id, dto } })
    else
      createMutation({ variables: { dto } })
  }

  const handleRemove = async (record: ShippingMethod) => {
    const ok = await confirm(translate('remove_confirmation_message', 'shipping_method', '?'))
    if (!ok) return
    await deleteMutation({ variables: { id: record.id } })
  }

  const closeRules = (changed?: boolean) => {
    setRulesFor(null)
    if (changed) refetch()
  }

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    setRows(prev => {
      const from = prev.findIndex(r => r.key === active.id)
      const to = prev.findIndex(r => r.key === over.id)
      const next = arrayMove(prev, from, to)
      reorderMutation({ variables: { dto: { ids: next.map(r => r.method.id) } } })
      return next
    })
  }

  const hasActiveFallback = rows.some(
    ({ method }) => method.kind === ShippingMethodKind.Fallback && method.isActive && !method.deletedAt,
  )

  const reorderDisabled = showDeleted

  return (
    <Card>
      {contextHolder}
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {translate('shipping_methods')}
        </Typography.Title>
        <Space>
          <Space>
            <Switch checked={showDeleted} onChange={setShowDeleted} />
            <span>{translate('show_deleted')}</span>
          </Space>
          <Tooltip title={translate('add')}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} />
          </Tooltip>
        </Space>
      </Space>

      {!loading && !hasActiveFallback && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={translate('no_active_fallback')}
          description={translate('no_active_fallback_description')}
        />
      )}

      <ShippingEligibilityPreview />

      <DraggableTable items={rows} onDragEnd={onDragEnd}>
        <Table<ShippingMethodEntry>
          rowKey="key"
          loading={loading}
          components={reorderDisabled ? undefined : { body: { row: DraggableRow } }}
          dataSource={rows}
          pagination={false}
          columns={[
            {
              key: 'sort',
              align: 'center',
              width: 40,
              render: () => (reorderDisabled ? null : <DragHandle />),
            },
            {
              key: 'title',
              title: translate('title'),
              render: (_, { method }) => (
                <Space direction="vertical" size={0}>
                  <Space size={6}>
                    <span>{method.title}</span>
                    {method.isSystem && <Tag color="purple">{translate('system')}</Tag>}
                  </Space>
                  {method.description && (
                    <Typography.Text type="secondary">{method.description}</Typography.Text>
                  )}
                </Space>
              ),
            },
            {
              key: 'methodKey',
              title: translate('key'),
              width: 190,
              render: (_, { method }) => <code>{method.key}</code>,
            },
            {
              key: 'kind',
              title: translate('shipping_method_kind'),
              width: 130,
              render: (_, { method }) => (
                <Tag color={SHIPPING_METHOD_KIND_COLOR[method.kind]}>
                  {SHIPPING_METHOD_KIND_LABEL[method.kind]}
                </Tag>
              ),
            },
            {
              key: 'basePrice',
              title: translate('base_price'),
              width: 110,
              align: 'right',
              render: (_, { method }) => `$${method.basePrice.toFixed(2)}`,
            },
            {
              key: 'rules',
              title: translate('availability_rules'),
              width: 190,
              render: (_, { method }) => {
                const active = method.rules.filter(rule => rule.isActive).length
                const inactive = method.rules.length - active
                return (
                  <Space size={4}>
                    {active === 0 ? (
                      <Tag color="green">{translate('available_everywhere')}</Tag>
                    ) : (
                      <Tag>{translate(String(active), active === 1 ? 'rule_count_one' : 'rules_count')}</Tag>
                    )}
                    {inactive > 0 && (
                      <Tag color="default">{translate(String(inactive), 'rules_disabled')}</Tag>
                    )}
                  </Space>
                )
              },
            },
            {
              key: 'isActive',
              title: translate('status'),
              width: 110,
              render: (_, { method }) =>
                method.deletedAt ? (
                  <Tag color="default">{translate('deleted')}</Tag>
                ) : (
                  <Switch
                    checked={method.isActive}
                    onChange={() => toggleMutation({ variables: { id: method.id } })}
                  />
                ),
            },
            {
              key: 'action',
              width: 140,
              render: (_, { method }) =>
                method.deletedAt ? (
                  <Tooltip title={translate('restore')}>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<UndoOutlined />}
                      onClick={() => restoreMutation({ variables: { id: method.id } })}
                    />
                  </Tooltip>
                ) : (
                  <Space>
                    <Tooltip title={translate('edit')}>
                      <Button
                        type="text"
                        shape="circle"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(method)}
                      />
                    </Tooltip>
                    <Tooltip title={translate('availability_rules')}>
                      <Button
                        type="text"
                        shape="circle"
                        icon={<ControlOutlined />}
                        onClick={() => setRulesFor(method)}
                      />
                    </Tooltip>
                    {!method.isSystem && (
                      <Tooltip title={translate('delete')}>
                        <Button
                          type="text"
                          shape="circle"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemove(method)}
                        />
                      </Tooltip>
                    )}
                  </Space>
                ),
            },
          ]}
        />
      </DraggableTable>

      <ShippingMethodModal
        open={modalOpen}
        editing={editing}
        saving={creating || updating}
        onCancel={closeModal}
        onSubmit={handleSubmit}
      />

      <ShippingMethodRulesDrawer method={rulesFor} onClose={closeRules} />
    </Card>
  )
}
